<template>
    <Modal ref="Modal" :title="translate('labels.edit_products')" size="lg" @on-hide="hide">
        <div class="modal-body">
            <div class="row mb-3" v-if="allChecked && selectedProductUuids.length < totalProducts">
                <div class="col-lg-6">
                    <InputGroup>
                        <Radio :model-value="editOption === 'selected'" @update:modelValue="editOption = 'selected'" />
                        <template #append>
                            {{ $t('labels.edit_only_selected_products', {total: selectedProductUuids.length}) }}
                        </template>
                    </InputGroup>
                </div>
                <div class="col-lg-6">
                    <InputGroup>
                        <Radio :model-value="editOption === 'all'" @update:modelValue="editOption = 'all'" />
                        <template #append>
                            {{ $t('labels.edit_all_products', {total: totalProducts}) }}
                        </template>
                    </InputGroup>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-bordered table-fixed">
                    <thead>
                        <tr>
                            <th width="50%">
                                {{ $t('labels.field') }}
                            </th>
                            <th width="50%">
                                {{ $t('labels.action') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(field, index) in fields">
                            <tr>
                                <td>{{ field.label }}</td>
                                <td>
                                    <AppSelect
                                        option-key="key"
                                        option-value="value"
                                        :model-value="field.selectedAction?.key"
                                        :selected-value="field.selectedAction?.value"
                                        :options="getFieldActions(field)"
                                        @update:model-value="selectAction($event, index)"
                                        can-be-empty
                                    />
                                </td>
                            </tr>
                            <FieldActionEditor
                                :action="field.selectedAction"
                                :type="field.type"
                                :add-select-options="addSelectOptions"
                                :replace-select-options="replaceSelectOptions"
                                v-model="field.dataForProcessing"
                            />
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="modal-footer">
            <Button
                @click="updateProducts"
                :disabled="isSaving"
                size="sm"
                color="indigo"
            >
                {{ translate('buttons.edit') }}
            </Button>
        </div>
    </Modal>
</template>
<script>
import Modal from '../../../../components/new/modal/AppModal.vue';
import AppSelect from '../../../../components/new/form/select/AppSelect.vue';
import Button from '../../../../components/new/button/AppButton.vue';
import FieldActionEditor from './components/FieldActionEditor.vue';
import Radio from '../../../../components/new/form/radio/Radio.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';

import { httpClient } from '../../../../services/HttpClient';

export default {
    name: "EditProductModal",
    components: {
        AppSelect, Modal, Button, FieldActionEditor, Radio, InputGroup
    },
    data() {
        return {
            isSaving: false,
            fields: [],
            addSelectOptions: [
                {key: 'prefix', value: this.$t('batchUpdateProductModal.add_options.prefix')},
                {key: 'suffix', value: this.$t('batchUpdateProductModal.add_options.suffix')},
            ],
            replaceSelectOptions: [
                {key: 'part', value: this.$t('batchUpdateProductModal.replace_options.part')},
                {key: 'prefix', value: this.$t('batchUpdateProductModal.replace_options.prefix')},
                {key: 'suffix', value: this.$t('batchUpdateProductModal.replace_options.suffix')}
            ],
            selectedProductUuids: [],
            filters: {},
            allChecked: false,
            totalProducts: 0,
            editOption: 'selected',
            uniqueFields: ['source_id', 'second_source_id', 'sku']
        }
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    emits: ['product-edited'],
    methods: {
        hide() {
            this.$refs.Modal.hide();
            this.selectedProductUuids = [];
            this.fields = [];

            this.editOption = 'selected';
            this.filters = {};
            this.allChecked = false;
            this.totalProducts = 0;
        },
        initializeFields() {
            const langs = this.integration.language_aliases || [];
            const langFields = [];
            
            langs.forEach(lang => {
                langFields.push({
                    name: `names.${lang}`,
                    label: this.$t('table.titles.name') + ` (${this.$t(`shortLang.${lang}`)})`,
                    selectedAction: null,
                    dataForProcessing: null,
                    type: 'text'
                });
            });
            langs.forEach(lang => {
                langFields.push({
                    name: `descriptions.${lang}`,
                    label: this.$t('table.titles.description') + ` (${this.$t(`shortLang.${lang}`)})`,
                    selectedAction: null,
                    dataForProcessing: null,
                    type: 'textarea'
                });
            });
            langs.forEach(lang => {
                langFields.push({
                    name: `seos.${lang}`,
                    label: this.$t('labels.product_keywords') + ` (${this.$t(`shortLang.${lang}`)})`,
                    selectedAction: null,
                    dataForProcessing: null,
                    type: 'tags'
                });
            });

            this.fields = [
                { name: 'source_id',                 label: `${this.$t('labels.external_id')} (${this.$t('labels.main')})`,      selectedAction: null, dataForProcessing: null, type: 'number' },
                { name: 'second_source_id',          label: `${this.$t('labels.external_id')} (${this.$t('labels.secondary')})`, selectedAction: null, dataForProcessing: null, type: 'number' },
                { name: 'sku',                       label: this.$t('labels.sku'),                                               selectedAction: null, dataForProcessing: null, type: 'text' },
                { name: 'integration_category_uuid', label: this.$t('labels.category'),                                          selectedAction: null, dataForProcessing: null, type: 'category_autocomplete' },
                { name: 'tag_uuids',                 label: this.$t('labels.tags'),                                              selectedAction: null, dataForProcessing: null, type: 'multi_select' },
                { name: 'group_key',                 label: this.$t('labels.group_id'),                                          selectedAction: null, dataForProcessing: null, type: 'text' },
                ...langFields,
                { name: 'price',                     label: this.$t('labels.price'),                                             selectedAction: null, dataForProcessing: null, type: 'number' },
                { name: 'sale_price',                label: this.$t('labels.sale_price'),                                        selectedAction: null, dataForProcessing: null, type: 'number' },
                { name: 'promo_price',               label: this.$t('labels.promoPrice'),                                        selectedAction: null, dataForProcessing: null, type: 'number' },
                { name: 'balance',                   label: this.$t('labels.balance'),                                           selectedAction: null, dataForProcessing: null, type: 'number' },
                { name: 'manufacturer_name',         label: this.$t('labels.manufacturer'),                                      selectedAction: null, dataForProcessing: null, type: 'text' },
                { name: 'export_enabled',            label: this.$t('labels.exportEnabled'),                                     selectedAction: null, dataForProcessing: null, type: 'checkbox' },
                { name: 'is_auto_price_update_enabled', label: this.$t('labels.auto_price_update_enabled'),                      selectedAction: null, dataForProcessing: null, type: 'checkbox' },
                { name: 'is_auto_balance_update_enabled', label: this.$t('labels.auto_balance_update_enabled'),                  selectedAction: null, dataForProcessing: null, type: 'checkbox' },
                { name: 'measurement',               label: this.$t('labels.measurement'),                                       selectedAction: null, dataForProcessing: null, type: 'text' },
            ];
        },
        getFieldActions(field) {
            let defaultActions = []

            const isUniqueField = this.uniqueFields.includes(field.name);
            const canChangeField = isUniqueField ? this.selectedProductUuids.length === 1 : this.selectedProductUuids.length >= 1;

            if (canChangeField) {
                defaultActions.push({key: 'change', value: this.$t('batchUpdateProductModal.actions.change')})
            }

            let namesFields = this.integration.language_aliases.map(lang => `names.${lang}`)
            let descriptionFields = this.integration.language_aliases.map(lang => `descriptions.${lang}`)

            if (!namesFields.includes(field.name) && field.type !== 'checkbox') {
                defaultActions.push({key: 'clear', value: this.$t('batchUpdateProductModal.actions.clear')})
            }

            if ([...namesFields, 'sku'].includes(field.name)) {
                defaultActions.push({key: 'add', value: this.$t('batchUpdateProductModal.actions.add')})
                defaultActions.push({key: 'replace', value: this.$t('batchUpdateProductModal.actions.replace')})
            }

            if (descriptionFields.includes(field.name)) {
                defaultActions.push({key: 'add_name', value: this.$t('batchUpdateProductModal.actions.add_name')})
            }

            if (field.name === 'tag_uuids') {
                defaultActions.push({key: 'add_to_current', value: this.$t('batchUpdateProductModal.actions.add')})
                defaultActions.push({key: 'remove_from_current', value: this.$t('batchUpdateProductModal.actions.remove')})
            }

            return defaultActions;
        },
        show(productUuids, filters, allChecked, totalProducts) {
             if (!productUuids.length && !allChecked) {
                return;
            }

            this.selectedProductUuids = productUuids;
            this.filters = filters || {};
            this.allChecked = allChecked || false;
            this.totalProducts = totalProducts || 0;
            
            this.initializeFields();
            this.$refs.Modal.show();
        },
        selectAction(action, index) {
            this.fields[index].selectedAction = action;

            const initializers = {
                change: () => ({ value: null }),
                add: () => ({ value: null, add_option: { key: 'prefix', value: this.$t('batchUpdateProductModal.add_options.prefix') } }),
                replace: () => ({ from: null, to: '', replace_option: {key: 'part', value: this.$t('batchUpdateProductModal.replace_options.part')} }),
                add_to_current: () => ({ value: null }),
                remove_from_current: () => ({ value: null }),
            };

            if (!action) {
                this.fields[index].dataForProcessing = null;
                return;
            }

            const init = initializers[action.key];
            this.fields[index].dataForProcessing = init ? init() : null;
        },
        buildOperation(field) {
            const { selectedAction, dataForProcessing } = field;
            const operation = { field: field.name, action: selectedAction.key };

            switch (selectedAction.key) {
                case 'change':
                    operation.value = this.buildChangeValue(field);
                    break;
                case 'add':
                    operation.text = dataForProcessing.value;
                    operation.position = dataForProcessing.add_option?.key;
                    break;
                case 'replace':
                    operation.from = dataForProcessing.from;
                    operation.to = dataForProcessing.to ?? '';
                    operation.mode = dataForProcessing.replace_option?.key;
                    break;
                case 'add_to_current':
                case 'remove_from_current':
                    operation.value = (dataForProcessing.value || []).map(tag => tag.uuid);
                    break;
            }

            return operation;
        },
        buildChangeValue(field) {
            const value = field.dataForProcessing.value;

            switch (field.type) {
                case 'checkbox':
                    return Boolean(value);
                case 'category_autocomplete':
                    return value?.uuid ?? null;
                case 'multi_select':
                    return (value || []).map(item => item.uuid);
                case 'number':
                    return value === null || value === undefined || value === '' ? null : String(value);
                default:
                    return value;
            }
        },
        buildSelection() {
            if (this.allChecked && this.editOption === 'all') {
                const { orders, limit, page, ...filters } = this.filters;

                return { ...filters, all: true };
            }

            return { uuids: this.selectedProductUuids };
        },
        async updateProducts() {
            const operations = this.fields
                .filter(field => field.selectedAction)
                .map(field => this.buildOperation(field));

            if (!operations.length) {
                return;
            }

            this.isSaving = true;

            try {
                await httpClient.post(`/integrations/${this.integration.uuid}/products/batch-update`, {
                    ...this.buildSelection(),
                    operations,
                });
            } catch (error) {
                return;
            } finally {
                this.isSaving = false;
            }

            this.$emit('product-edited');
            this.hide();
        },
    },
}
</script>